import { type EventKind, addDays, todayIso, weekStart } from '@shkola/api';
import type { SubjectIcon } from '@shkola/design';
import { ArrowsLeftRightIcon } from '@phosphor-icons/react/ArrowsLeftRight';
import { ShieldIcon } from '@phosphor-icons/react/Shield';
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DARK,
  EventArt,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  SubjectArt,
  Tabs,
  grad,
  ringSmall,
} from '../design/forms';
import {
  CaretLeftIcon,
  CaretRightIcon,
  CheckIcon,
  ExamIcon,
  type FilledIcon,
  VideoCameraIcon,
} from '../design/icons';
import { useMinute } from '../lib/now';
import { dayLong, dayMonthLong, isoWeekday, monthName, weekdayShort } from '../student/home/model';
import { FLAT } from '../student/home/ShapesArt';

/*
 * «Розклад» as a planner (28.10, his refs «Schedule 01-07 January» and «October 2023 · week»: «как людям
 * привычнее — планировщики, календари», after the month-and-a-day that missed it). The week on a time
 * axis: the hours on the left, the days as columns (the day in focus filled dark), every lesson a soft
 * block of its subject's gradient exactly from its start to its end — the subject, the time, a small
 * white mark for a test, a substitute, an online lesson; the breaks are simply the gaps; «now» a line
 * across today with the time on the axis; the holidays tinted mint; a school event with a time is a
 * block in its hour as a lesson is (his «почему вверху узкой плашкой, а не в своём времени»), one for the
 * whole day — a note over the day. Over it the range of the week big and light, «Сьогодні», ‹ ›, the
 * views «Місяць / Тиждень / День» and the chips of the subjects, wrapped — no scroll bar (his «огромный
 * скроллбар не нужен»: one row, ‹ › at its end when it does not fit). A block is tall enough for a long
 * subject on two lines. On a phone — the day, with the week's days on top to switch. One planner for every role: what a block says and what a tap opens is the page's.
 */

export type PlanView = 'month' | 'week' | 'day';
export type PlanMark = 'test' | 'substitute' | 'online' | 'shelter';

/** One thing on the planner: a lesson, a club or a school event. */
export interface PlanItem {
  id: string;
  date: string;
  /** minutes from midnight */
  start: number;
  end: number;
  title: string;
  /** under the title: the room and the teacher, or the class */
  sub?: string;
  tone: Grad;
  /** what the chips show one of (the subject, the class) */
  group?: string;
  marks: ReadonlyArray<PlanMark>;
  cancelled?: boolean;
  /** the subject's picture, quiet behind a lesson of the day */
  icon?: SubjectIcon | undefined;
  /** a club after the lessons: its own word under the title */
  club?: boolean;
  /** a school event: its picture in the day, its icon in the week */
  event?: EventKind;
  badge?: FilledIcon;
  onOpen?: (el: HTMLElement) => void;
}

/** A day's note over the grid: a school event, or the holidays (the day tinted mint). */
export interface PlanNote {
  id: string;
  startsOn: string;
  endsOn: string;
  title: string;
  tone: Grad;
  holiday: boolean;
  time?: string | null | undefined;
  onOpen?: () => void;
}

/** «08:30» or «08:30:00» → minutes from midnight */
export const minutesOf = (time: string) => {
  const [h = '0', m = '0'] = time.split(':');
  return Number(h) * 60 + Number(m);
};
const clockOf = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/** a weekend day → the Monday after it */
export const schoolDay = (date: string) =>
  isoWeekday(date) >= 6 ? addDays(weekStart(date), 7) : date;

const firstOfMonth = (date: string, shift = 0) => {
  const d = new Date(`${date.slice(0, 7)}-01T12:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + shift);
  return d.toISOString().slice(0, 10);
};

/** The planner's state: the view, the day in focus, the dates to load. */
export function usePlanner(initial: string | undefined) {
  const today = todayIso();
  const [view, setView] = useState<PlanView>('week');
  const [picked, setPicked] = useState<string>();
  const date = picked ?? initial ?? schoolDay(today);
  const monday = weekStart(date);
  const month = firstOfMonth(date);
  // the whole month around the day (its weeks): the week, the day, the month and the small month beside
  // them all come from one load
  const range = {
    from: weekStart(month),
    to: addDays(weekStart(addDays(firstOfMonth(month, 1), -1)), 6),
  };
  return {
    view,
    setView,
    date,
    today,
    monday,
    month,
    range,
    pick: (d: string, next?: PlanView) => {
      setPicked(d);
      if (next) setView(next);
    },
    goToday: () => setPicked(today),
    shift: (dir: 1 | -1, shown: PlanView) => {
      if (shown === 'month') setPicked(firstOfMonth(date, dir));
      else if (shown === 'week') setPicked(addDays(monday, dir * 7));
      else {
        // a day at a time, over the weekend
        let d = addDays(date, dir);
        while (isoWeekday(d) >= 6) d = addDays(d, dir);
        setPicked(d);
      }
    },
  };
}
export type Planner = ReturnType<typeof usePlanner>;

/** From a phone's width on, the week fits; narrower — the day. */
function useWide() {
  const query = '(min-width: 768px)';
  const [wide, setWide] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia(query).matches,
  );
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return wide;
}

type MarkKey =
  'schedule.test' | 'studentHome.substitute' | 'studentHome.online' | 'studentHome.shelter';
const MARK: Record<PlanMark, { icon: FilledIcon; tone: Grad; key: MarkKey }> = {
  test: { icon: ExamIcon, tone: 'coral', key: 'schedule.test' },
  substitute: { icon: ArrowsLeftRightIcon, tone: 'lavender', key: 'studentHome.substitute' },
  online: { icon: VideoCameraIcon, tone: 'sky', key: 'studentHome.online' },
  shelter: { icon: ShieldIcon, tone: 'apricot', key: 'studentHome.shelter' },
};

/** the height of an hour on the grid */
const HOUR = 80;
/** the column of the hours */
const AXIS = 56;

/**
 * Things at the same time in a day (his ref «Math Exam / Art Exam», 28.10, after the side-by-side event
 * was «очень странное решение»): each keeps the column's full width, the one that starts later lies over
 * the earlier one, so every block shows its title; only things that start together share the column.
 * `shown` — how much of a block shows above the next one over it (its words are fitted to that).
 */
function laneOut(items: ReadonlyArray<PlanItem>) {
  const out = new Map<string, { lane: number; of: number; layer: number; shown: number }>();
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end);
  // together: starting within a quarter of an hour of each other
  const TOGETHER = 15;
  let i = 0;
  while (i < sorted.length) {
    const first = sorted[i] as PlanItem;
    const group = [first];
    while (i + group.length < sorted.length) {
      const next = sorted[i + group.length] as PlanItem;
      if (next.start - first.start >= TOGETHER) break;
      group.push(next);
    }
    // the next thing starting later covers the bottom of this group
    const after = sorted[i + group.length];
    group.forEach((item, lane) => {
      const covered = after && after.start < item.end ? after.start : item.end;
      out.set(item.id, { lane, of: group.length, layer: i + 1, shown: covered - item.start });
    });
    i += group.length;
  }
  return out;
}

/** The title of the range: «2 – 6 листопада», «30 жовтня – 3 листопада», a day, a month. */
function rangeTitle(view: PlanView, planner: Planner, days: ReadonlyArray<string>) {
  if (view === 'day') return dayLong(planner.date);
  if (view === 'month') return `${monthName(planner.month)} ${planner.month.slice(0, 4)}`;
  const first = days[0] ?? planner.monday;
  const last = days.at(-1) ?? first;
  return first.slice(0, 7) === last.slice(0, 7)
    ? `${Number(first.slice(8))} – ${dayMonthLong(last)}`
    : `${dayMonthLong(first)} – ${dayMonthLong(last)}`;
}

/** A soft white mark in a block: the icon of a test, a substitute, an online lesson. */
function MarkIcon({ mark, label }: { mark: PlanMark; label: string }) {
  const { icon: Icon, tone } = MARK[mark];
  return (
    <span
      title={label}
      aria-label={label}
      className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-white"
      style={{ color: GRAD[tone].ink }}
    >
      <Icon size={11} weight="fill" aria-hidden />
    </span>
  );
}

/** A mark with its word (the day's wide blocks). */
function MarkWord({ mark }: { mark: PlanMark }) {
  const { t } = useTranslation();
  const { icon: Icon, tone } = MARK[mark];
  return (
    <span
      className="inline-flex h-[22px] shrink-0 items-center gap-1 rounded-[7px] bg-white px-1.5 text-[11.5px] leading-none font-medium"
      style={{ color: GRAD[tone].ink }}
    >
      <Icon size={12} weight="fill" aria-hidden />
      {t(MARK[mark].key)}
    </span>
  );
}

/** One block on the grid. */
function Block({
  item,
  height,
  wide,
  live,
  past,
  faded,
  stacked = false,
}: {
  item: PlanItem;
  height: number;
  wide: boolean;
  live: boolean;
  past: boolean;
  faded: boolean;
  /** lies over an earlier block: a white edge between them */
  stacked?: boolean;
}) {
  const { t } = useTranslation();
  const tone: Grad = item.cancelled ? 'stone' : item.tone;
  const time = `${clockOf(item.start)} – ${clockOf(item.end)}`;
  const short = height < 44;
  const style: CSSProperties = {
    backgroundImage: grad(tone),
    color: GRAD[tone].ink,
    opacity: faded ? 0.28 : past ? 0.62 : 1,
    boxShadow: [live && `inset 0 0 0 2px ${DARK}`, stacked && '0 0 0 2px #FFFFFF']
      .filter(Boolean)
      .join(', '),
  };
  return (
    <button
      type="button"
      onClick={(e) => item.onOpen?.(e.currentTarget)}
      disabled={!item.onOpen}
      title={`${item.title} · ${time}${item.sub ? ` · ${item.sub}` : ''}`}
      className={`relative isolate flex size-full flex-col overflow-hidden text-left transition-[opacity,filter] duration-200 enabled:hover:brightness-[0.98] ${
        wide ? 'rounded-[16px] px-3.5 py-2' : 'rounded-[12px] px-2 py-1.5'
      }`}
      style={style}
    >
      {wide && item.event && (
        <EventArt
          kind={item.event}
          tone={tone}
          size={Math.min(110, Math.max(64, height + 30))}
          ghost
          className="pointer-events-none absolute top-1/2 right-6 -z-10 -translate-y-1/2"
        />
      )}
      {wide && item.icon && (
        <SubjectArt
          icon={item.icon}
          tone={tone}
          size={Math.min(110, Math.max(64, height + 30))}
          ghost
          className="pointer-events-none absolute top-1/2 right-6 -z-10 -translate-y-1/2"
        />
      )}
      <span
        className={`flex min-w-0 gap-1 ${wide ? 'items-center gap-2' : 'items-start [&>*:first-child]:flex-1'}`}
      >
        <span
          // a long subject goes on two lines where the block is tall enough
          className={`min-w-0 font-medium ${wide ? 'truncate text-[15px] leading-[22px]' : `text-[12.5px] leading-4 ${height >= 58 && !short ? 'line-clamp-2' : 'truncate'}`} ${item.cancelled ? 'line-through' : ''}`}
          style={{ color: GRAD[tone].ink }}
        >
          {item.title}
          {short && <span className="font-normal opacity-75"> · {clockOf(item.start)}</span>}
        </span>
        {/* the day's wide block: the marks with their words beside the title */}
        {wide && (item.marks.length > 0 || item.cancelled) && (
          <span className="flex shrink-0 gap-1">
            {item.cancelled ? (
              <span
                className="inline-flex h-[22px] items-center rounded-[7px] bg-white px-1.5 text-[11.5px] font-medium"
                style={{ color: INK_SOFT }}
              >
                {t('schedule.cancelled')}
              </span>
            ) : (
              item.marks.map((m) => <MarkWord key={m} mark={m} />)
            )}
          </span>
        )}
        {!wide && (item.marks.length > 0 || item.badge) && (
          <span className="-mt-0.5 -mr-0.5 flex shrink-0 gap-0.5">
            {item.badge && (
              <span
                aria-hidden
                className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-white"
                style={{ color: GRAD[tone].ink }}
              >
                <item.badge size={11} weight="fill" />
              </span>
            )}
            {item.marks.map((m) => (
              <MarkIcon key={m} mark={m} label={t(MARK[m].key)} />
            ))}
          </span>
        )}
      </span>
      {!short && (
        <span
          className={`truncate ${wide ? 'text-[13px] leading-[18px]' : 'text-[11.5px] leading-[15px]'}`}
          style={{ color: GRAD[tone].ink, opacity: 0.75 }}
        >
          {wide
            ? [time, item.club ? t('studentHome.glass.week.club') : item.sub]
                .filter(Boolean)
                .join(' · ')
            : time}
        </span>
      )}
      {!wide && !short && height >= 70 && (item.sub || item.club) && (
        <span className="truncate text-[11.5px] leading-[15px]" style={{ color: INK_SOFT }}>
          {item.club ? t('studentHome.glass.week.club') : item.sub}
        </span>
      )}
    </button>
  );
}

/** A day's head: the weekday small, the date big; the day in focus filled dark. */
function DayHead({
  date,
  focus,
  today,
  holiday,
  onClick,
  compact = false,
}: {
  date: string;
  focus: boolean;
  today: boolean;
  holiday: boolean;
  onClick: () => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={focus}
      title={dayLong(date)}
      className={`flex h-12 min-w-0 items-center justify-center rounded-[14px] transition-colors ${focus ? '' : 'hover:bg-[#F5F3EF]'}`}
      style={
        focus
          ? { backgroundColor: DARK, color: '#FFFFFF' }
          : {
              color: today ? '#E8551F' : INK,
              backgroundColor: holiday ? GRAD.mint.from : undefined,
            }
      }
    >
      {/* the words in the middle of the tile, the weekday on the line of the date */}
      <span
        className={`flex min-w-0 ${compact ? 'flex-col items-center' : 'items-baseline gap-1.5'}`}
      >
        <span
          className="text-[12.5px] leading-4 font-medium"
          style={{ color: focus ? 'rgb(255 255 255 / 0.7)' : today ? '#E8551F' : INK_FAINT }}
        >
          {today && !compact ? t('schedule.today') : weekdayShort(date)}
        </span>
        <span
          className={`font-display font-light tracking-[-0.03em] ${compact ? 'text-[20px] leading-6' : 'text-[26px] leading-7'}`}
        >
          {Number(date.slice(8))}
        </span>
      </span>
    </button>
  );
}

/** The events of a whole day over its column (an event with a time stands in its hour). */
function NoteChips({ notes, wide }: { notes: ReadonlyArray<PlanNote>; wide: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {notes.map((n) => (
        <button
          key={n.id}
          type="button"
          onClick={n.onOpen}
          disabled={!n.onOpen}
          title={n.title}
          className={`flex h-8 min-w-0 items-center gap-1 rounded-[10px] px-2.5 text-left text-[12.5px] font-medium ${wide ? 'self-start' : ''}`}
          style={{ backgroundImage: grad(n.tone), color: GRAD[n.tone].ink }}
        >
          {n.time && <span className="shrink-0 opacity-75">{n.time}</span>}
          <span className="truncate">{n.title}</span>
        </button>
      ))}
    </div>
  );
}

/** The days on the time axis (the week, or the one day). */
function TimeGrid({
  days,
  items,
  notes,
  today,
  group,
  single,
  onDay,
}: {
  days: ReadonlyArray<string>;
  items: ReadonlyArray<PlanItem>;
  notes: ReadonlyArray<PlanNote>;
  today: string;
  group: string | null;
  single: boolean;
  onDay: (date: string) => void;
}) {
  const { t } = useTranslation();
  const now = useMinute();
  const nowMin = minutesOf(
    new Intl.DateTimeFormat('uk-UA', {
      timeZone: 'Europe/Kyiv',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(new Date(now)),
  );
  const shown = items.filter((i) => days.includes(i.date));
  const from = Math.min(8, ...shown.map((i) => Math.floor(i.start / 60)));
  const to = Math.max(15, ...shown.map((i) => Math.ceil(i.end / 60)));
  const hours = Array.from({ length: to - from + 1 }, (_, i) => from + i);
  const height = (to - from) * HOUR;
  const y = (min: number) => ((min - from * 60) / 60) * HOUR;
  const notesOn = (d: string) => notes.filter((n) => n.startsOn <= d && d <= n.endsOn);
  const holidayOn = (d: string) => notesOn(d).some((n) => n.holiday);
  const dayNotes = days.map((d) => notesOn(d).filter((n) => !n.holiday));
  const anyNotes = dayNotes.some((n) => n.length > 0);
  const cols = `${AXIS}px repeat(${days.length}, minmax(0, 1fr))`;
  const showNow = days.includes(today) && nowMin >= from * 60 && nowMin <= to * 60;
  const empty = shown.length === 0;
  return (
    <div className="min-w-0">
      {!single && (
        <div className="grid gap-x-1.5 pb-2" style={{ gridTemplateColumns: cols }}>
          <span />
          {days.map((d) => (
            <DayHead
              key={d}
              date={d}
              focus={d === today}
              today={d === today}
              holiday={holidayOn(d)}
              onClick={() => onDay(d)}
            />
          ))}
        </div>
      )}
      {anyNotes && (
        <div className="grid items-center gap-x-1.5 pb-2" style={{ gridTemplateColumns: cols }}>
          <span className="pr-2 text-right text-[11.5px] leading-4" style={{ color: INK_FAINT }}>
            {t('schedule.allDay')}
          </span>
          {dayNotes.map((n, i) => (
            <NoteChips key={days[i]} notes={n} wide={single} />
          ))}
        </div>
      )}
      <div className="grid gap-x-1.5" style={{ gridTemplateColumns: cols }}>
        {/* the hours */}
        <div className="relative" style={{ height }}>
          {hours.map((h, i) => (
            <span
              key={h}
              className="absolute right-2 -translate-y-1/2 text-[12px] leading-none"
              style={{ top: i * HOUR, color: INK_FAINT }}
            >
              {`${String(h).padStart(2, '0')}:00`}
            </span>
          ))}
          {showNow && (
            <span
              className="absolute right-0.5 z-10 -translate-y-1/2 rounded-[7px] px-1.5 py-1 text-[11.5px] leading-none font-semibold"
              style={{ top: y(nowMin), backgroundColor: FLAT.lime, color: INK }}
            >
              {clockOf(nowMin)}
            </span>
          )}
        </div>
        {days.map((d) => {
          const own = shown.filter((i) => i.date === d);
          const lanes = laneOut(own);
          const holiday = holidayOn(d);
          return (
            <div
              key={d}
              className="relative rounded-[14px]"
              style={{
                height,
                backgroundColor: holiday
                  ? GRAD.mint.from
                  : d === today && !single
                    ? 'rgb(245 243 239 / 0.6)'
                    : undefined,
              }}
            >
              {hours.map((h, i) => (
                <span
                  key={h}
                  aria-hidden
                  className="absolute inset-x-0 h-px"
                  style={{ top: i * HOUR, backgroundColor: LINE }}
                />
              ))}
              {holiday && (
                <span
                  className="absolute inset-x-2 top-3 truncate text-center text-[12.5px] font-medium"
                  style={{ color: GRAD.mint.ink }}
                >
                  {notesOn(d).find((n) => n.holiday)?.title}
                </span>
              )}
              {own.map((item) => {
                const lane = lanes.get(item.id) ?? { lane: 0, of: 1, layer: 1, shown: 0 };
                // air between blocks that follow one another without a break
                const top = y(item.start) + 3;
                const h = Math.max(22, y(item.end) - y(item.start) - 6);
                const shown = Math.max(22, Math.min(h, (lane.shown / 60) * HOUR - 6));
                const live =
                  d === today && !item.cancelled && item.start <= nowMin && nowMin < item.end;
                const past = d < today || (d === today && item.end <= nowMin);
                return (
                  <div
                    key={item.id}
                    className="absolute px-[2px]"
                    style={{
                      top,
                      height: h,
                      left: `${(lane.lane / lane.of) * 100}%`,
                      width: `${100 / lane.of}%`,
                      zIndex: lane.layer,
                    }}
                  >
                    <Block
                      item={item}
                      height={shown}
                      stacked={
                        lane.layer > 1 &&
                        own.some((o) => o.start < item.start && o.end > item.start)
                      }
                      wide={single}
                      live={live}
                      past={past && !live}
                      faded={group !== null && item.group !== group}
                    />
                  </div>
                );
              })}
              {d === today && showNow && (
                <span
                  aria-hidden
                  // under the blocks: it must not strike the lesson going on through
                  className="pointer-events-none absolute inset-x-0 z-0 flex items-center"
                  style={{ top: y(nowMin) - 3 }}
                >
                  <span
                    className="size-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: INK }}
                  />
                  <span className="h-[1.5px] flex-1" style={{ backgroundColor: INK }} />
                </span>
              )}
            </div>
          );
        })}
      </div>
      {empty && !days.some(holidayOn) && (
        <p className="pt-4 text-center text-[14px]" style={{ color: INK_SOFT }}>
          {t('schedule.noLessons')}
        </p>
      )}
    </div>
  );
}

/** The month: a cell a day — the tests and the events in it, how many lessons; a tap opens the day. */
function MonthView({
  planner,
  items,
  notes,
  onPick,
}: {
  planner: Planner;
  items: ReadonlyArray<PlanItem>;
  notes: ReadonlyArray<PlanNote>;
  onPick: (date: string) => void;
}) {
  const { t } = useTranslation();
  const days: string[] = [];
  for (let d = planner.range.from; d <= planner.range.to; d = addDays(d, 1)) days.push(d);
  return (
    <div className="grid grid-cols-7 gap-1.5">
      {days.slice(0, 7).map((d) => (
        <span
          key={d}
          className="pb-1 text-center text-[12px] font-medium"
          style={{ color: INK_FAINT }}
        >
          {weekdayShort(d)}
        </span>
      ))}
      {days.map((d) => {
        const own = items.filter((i) => i.date === d && !i.club && !i.event);
        const held = own.filter((i) => !i.cancelled);
        const happenings = items.filter((i) => i.date === d && i.event);
        const tests = held.filter((i) => i.marks.includes('test'));
        const dayNotes = notes.filter((n) => n.startsOn <= d && d <= n.endsOn);
        const holiday = dayNotes.some((n) => n.holiday);
        const outside = d.slice(0, 7) !== planner.month.slice(0, 7);
        const isToday = d === planner.today;
        const chips = [
          ...tests.map((i) => ({
            key: i.id,
            text: `${t('schedule.test')} · ${i.title}`,
            tone: 'coral' as Grad,
          })),
          ...happenings.map((i) => ({ key: i.id, text: i.title, tone: i.tone })),
          ...dayNotes
            .filter((n) => !n.holiday)
            .map((n) => ({ key: n.id, text: n.title, tone: n.tone })),
        ];
        return (
          <button
            key={d}
            type="button"
            onClick={() => onPick(d)}
            aria-label={dayLong(d)}
            className="flex min-h-[64px] min-w-0 flex-col gap-1 rounded-[14px] p-1.5 text-left transition-colors hover:bg-[#F5F3EF] md:min-h-[112px] md:p-2"
            style={{
              backgroundColor: holiday ? GRAD.mint.from : undefined,
              boxShadow: `inset 0 0 0 1px ${LINE}`,
              opacity: outside ? 0.4 : 1,
            }}
          >
            <span className="flex items-center justify-between gap-1">
              <span
                className="flex size-7 items-center justify-center rounded-full text-[14px] font-medium"
                style={
                  isToday
                    ? { backgroundColor: DARK, color: '#FFFFFF' }
                    : { color: held.length ? INK : INK_FAINT }
                }
              >
                {Number(d.slice(8))}
              </span>
              {held.length > 0 && (
                <span className="hidden text-[11.5px] md:inline" style={{ color: INK_FAINT }}>
                  {t('student.lessonsCount', { count: held.length })}
                </span>
              )}
            </span>
            {holiday && (
              <span className="truncate text-[11.5px] font-medium" style={{ color: GRAD.mint.ink }}>
                {dayNotes.find((n) => n.holiday)?.title}
              </span>
            )}
            {/* on a phone — dots; wider — the words */}
            <span className="flex gap-1 md:hidden">
              {chips.slice(0, 3).map((c) => (
                <span
                  key={c.key}
                  className="size-1.5 rounded-full"
                  style={{ backgroundColor: c.tone === 'coral' ? FLAT.orange : FLAT.cobalt }}
                />
              ))}
            </span>
            <span className="hidden min-w-0 flex-col gap-1 md:flex">
              {chips.slice(0, 2).map((c) => (
                <span
                  key={c.key}
                  className="truncate rounded-[7px] px-1.5 py-1 text-[11.5px] leading-none font-medium"
                  style={{ backgroundImage: grad(c.tone), color: GRAD[c.tone].ink }}
                >
                  {c.text}
                </span>
              ))}
              {chips.length > 2 && (
                <span className="text-[11.5px]" style={{ color: INK_SOFT }}>
                  {t('schedule.more', { count: chips.length - 2 })}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * The chips in one row (his «огромный скроллбар не нужен», 28.10): no scroll bar; when they do not fit,
 * the row fades at its edge and ‹ › at its end move it.
 */
function ChipRow({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () =>
      setEdges({
        left: el.scrollLeft > 2,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
      });
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, []);
  const move = (dir: 1 | -1) =>
    ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.7, behavior: 'smooth' });
  const fade = `linear-gradient(90deg, ${edges.left ? 'transparent 0, #000 48px' : '#000 0'}, ${
    edges.right ? '#000 calc(100% - 48px), transparent 100%' : '#000 100%'
  })`;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div
        ref={ref}
        className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ maskImage: fade, WebkitMaskImage: fade }}
      >
        {children}
      </div>
      {(edges.left || edges.right) && (
        <span className="flex shrink-0 gap-1.5">
          <button
            type="button"
            className={`${ringSmall} disabled:opacity-30`}
            disabled={!edges.left}
            aria-label={t('schedule.chipsBack')}
            onClick={() => move(-1)}
          >
            <CaretLeftIcon size={13} weight="bold" aria-hidden />
          </button>
          <button
            type="button"
            className={`${ringSmall} disabled:opacity-30`}
            disabled={!edges.right}
            aria-label={t('schedule.chipsMore')}
            onClick={() => move(1)}
          >
            <CaretRightIcon size={13} weight="bold" aria-hidden />
          </button>
        </span>
      )}
    </div>
  );
}

/**
 * The planner in a white block: the range, «Сьогодні», ‹ ›, the views; the chips of the subjects; the
 * month, the week or the day. `days` — the columns of the week (Monday to Friday, the weekend when it
 * has lessons).
 */
export function PlannerBlock({
  planner,
  items,
  notes,
  pending = false,
  groupTone,
}: {
  planner: Planner;
  items: ReadonlyArray<PlanItem>;
  notes: ReadonlyArray<PlanNote>;
  pending?: boolean;
  /** the colour of a chip's dot (the subject's) */
  groupTone?: (group: string) => Grad | undefined;
}) {
  const { t } = useTranslation();
  const wide = useWide();
  const [group, setGroup] = useState<string | null>(null);
  const view: PlanView = planner.view === 'week' && !wide ? 'day' : planner.view;
  const weekend = [5, 6].filter((i) => items.some((it) => it.date === addDays(planner.monday, i)));
  const weekDays = [0, 1, 2, 3, 4, ...weekend].map((i) => addDays(planner.monday, i));
  const groups = useMemo(
    () =>
      [
        ...new Set(
          items
            .filter((i) => weekDays.includes(i.date) && i.group && !i.club)
            .map((i) => i.group as string),
        ),
      ].sort((a, b) => a.localeCompare(b, 'uk')),
    // the week's subjects
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, planner.monday],
  );
  const atToday =
    view === 'month'
      ? planner.month === planner.today.slice(0, 7) + '-01'
      : view === 'week'
        ? planner.monday === weekStart(planner.today)
        : planner.date === planner.today;
  const options = (wide ? (['month', 'week', 'day'] as const) : (['month', 'day'] as const)).map(
    (id) => ({ id, label: t(`schedule.views.${id}`) }),
  );

  return (
    <section
      className="flex min-w-0 flex-col gap-4 rounded-[28px] bg-white p-4 md:p-6"
      style={{ color: INK }}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
        <h2 className="min-w-0 font-display text-[26px] leading-8 font-light tracking-[-0.035em] md:text-[32px] md:leading-10">
          {rangeTitle(view, planner, weekDays)}
        </h2>
        <span className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={planner.goToday}
            disabled={atToday}
            className="inline-flex h-8 items-center rounded-full px-3.5 text-[13px] font-medium text-white disabled:cursor-default"
            style={{ backgroundColor: DARK }}
          >
            {t('schedule.today')}
          </button>
          <button
            type="button"
            className={ringSmall}
            aria-label={t(`schedule.prev.${view}`)}
            title={t(`schedule.prev.${view}`)}
            onClick={() => planner.shift(-1, view)}
          >
            <CaretLeftIcon size={13} weight="bold" aria-hidden />
          </button>
          <button
            type="button"
            className={ringSmall}
            aria-label={t(`schedule.next.${view}`)}
            title={t(`schedule.next.${view}`)}
            onClick={() => planner.shift(1, view)}
          >
            <CaretRightIcon size={13} weight="bold" aria-hidden />
          </button>
        </span>
        <span className="ml-auto">
          <Tabs
            label={t('schedule.title')}
            value={view}
            onChange={(v) => planner.setView(v)}
            options={options}
          />
        </span>
      </div>

      {view !== 'month' && groups.length > 1 && (
        <ChipRow>
          {[null, ...groups].map((g) => {
            const on = g === group;
            const tone = g ? groupTone?.(g) : undefined;
            return (
              <button
                key={g ?? 'all'}
                type="button"
                aria-pressed={on}
                onClick={() => setGroup(g)}
                className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full px-3.5 text-[13px] font-medium whitespace-nowrap transition-colors"
                style={{ backgroundColor: on ? '#E9E5DC' : PAPER, color: INK }}
              >
                {g === null ? (
                  on && <CheckIcon size={13} weight="bold" aria-hidden />
                ) : tone ? (
                  <span
                    aria-hidden
                    className="size-2.5 rounded-full"
                    style={{
                      backgroundImage: `linear-gradient(160deg, ${GRAD[tone].to}, ${GRAD[tone].ink})`,
                    }}
                  />
                ) : null}
                {g ?? t('schedule.all')}
              </button>
            );
          })}
        </ChipRow>
      )}

      {/* the day on a phone: the week's days on top to switch */}
      {view === 'day' && (
        <div
          className="grid gap-1.5"
          style={{ gridTemplateColumns: `repeat(${weekDays.length}, minmax(0, 1fr))` }}
        >
          {weekDays.map((d) => (
            <DayHead
              key={d}
              date={d}
              focus={d === planner.date}
              today={d === planner.today}
              holiday={notes.some((n) => n.holiday && n.startsOn <= d && d <= n.endsOn)}
              onClick={() => planner.pick(d)}
              compact
            />
          ))}
        </div>
      )}

      <div
        aria-busy={pending}
        className={`min-w-0 ${pending ? 'opacity-50 transition-opacity' : ''}`}
      >
        <div className="min-w-0">
          {view === 'month' ? (
            <MonthView
              planner={planner}
              items={items}
              notes={notes}
              onPick={(d) => planner.pick(d, 'day')}
            />
          ) : (
            <TimeGrid
              days={view === 'day' ? [planner.date] : weekDays}
              items={items}
              notes={notes}
              today={planner.today}
              group={group}
              single={view === 'day'}
              onDay={(d) => planner.pick(d, 'day')}
            />
          )}
        </div>
      </div>
    </section>
  );
}
